
import { TFunction } from 'i18next';
import { ApiError } from '../lib/apiClient';

export interface FriendlyStatusMessage {
  status: 'success' | 'error' | 'warning';
  title: string;
  message: string;
  suggestion: string | null;
  technical_reference?: string;
  language: string;
}

/**
 * Middleware de Comunicação de API focado em Experiência do Usuário (UX).
 * Transforma respostas brutas de APIs em mensagens claras e amigáveis.
 */
export function transformApiResponse(
  error: unknown,
  t: TFunction,
  language: string = 'pt-BR'
): FriendlyStatusMessage {

  // Default values
  let result: FriendlyStatusMessage = {
    status: 'error',
    title: t('apiMiddleware.defaultErrorTitle', 'Erro na operação'),
    message: t('apiMiddleware.defaultErrorMessage', 'Ocorreu um problema inesperado ao processar sua solicitação.'),
    suggestion: t('apiMiddleware.defaultErrorSuggestion', 'Tente novamente em alguns instantes.'),
    technical_reference: undefined,
    language
  };

  // Handle Strings
  if (typeof error === 'string') {
    result.technical_reference = 'String Error';
    // Check for common strings if needed, or just use as message if safe
    // Ideally we avoid showing raw strings unless we know them
    result.message = error; // Use with caution, maybe map known strings
    return result;
  }

  // Handle ApiError (Custom class likely used in apiClient)
  if (error instanceof ApiError) {
    // Check for invalid credentials BEFORE generic 401 handling
    if (error.status === 401 && error.message) {
      const lowerMsg = error.message.toLowerCase();
      if (lowerMsg.includes('invalid username or password') ||
          lowerMsg.includes('invalid credentials') ||
          lowerMsg.includes('usuário ou senha')) {
        result.title = t('login.errors.authFailed', 'Falha na autenticação');
        result.message = t('login.errors.failed', 'Usuário ou senha incorretos.');
        result.suggestion = t('login.errors.suggestion', 'Verifique suas credenciais e tente novamente.');
        result.technical_reference = 'HTTP 401 - Invalid Credentials';
        return result;
      }
    }

    if (error.status === 401) {
      result.title = t('apiMiddleware.authTitle', 'Acesso não autorizado');
      result.message = t('apiMiddleware.authMessage', 'Sua sessão expirou ou você não tem permissão para esta ação.');
      result.suggestion = t('apiMiddleware.authSuggestion', 'Faça login novamente.');
      result.technical_reference = 'HTTP 401';
      return result;
    }

    if (error.status === 403) {
      result.title = t('apiMiddleware.forbiddenTitle', 'Permissão negada');
      result.message = t('apiMiddleware.forbiddenMessage', 'Você não tem as permissões necessárias para acessar este recurso.');
      result.suggestion = t('apiMiddleware.forbiddenSuggestion', 'Entre em contato com o administrador.');
      result.technical_reference = 'HTTP 403';
      return result;
    }

    if (error.status === 404) {
      result.title = t('apiMiddleware.notFoundTitle', 'Recurso não encontrado');
      result.message = t('apiMiddleware.notFoundMessage', 'O item que você está tentando acessar não existe ou foi removido.');
      result.suggestion = null;
      result.technical_reference = 'HTTP 404';
      return result;
    }

    if (error.status === 500) {
      result.title = t('apiMiddleware.serverErrorTitle', 'Erro no servidor');
      result.message = t('apiMiddleware.serverErrorMessage', 'Nossos servidores encontraram uma dificuldade técnica.');
      result.suggestion = t('apiMiddleware.serverErrorSuggestion', 'Aguarde alguns minutos e tente novamente.');
      result.technical_reference = 'HTTP 500';
      return result;
    }

    if (error.status === 408) {
      result.title = t('apiMiddleware.timeoutTitle', 'Tempo esgotado');
      result.message = t('apiMiddleware.timeoutMessage', 'O servidor demorou muito para responder.');
      result.suggestion = t('apiMiddleware.timeoutSuggestion', 'Tente novamente em alguns instantes.');
      result.technical_reference = 'HTTP 408';
      return result;
    }

    if (error.status === 409) {
      result.title = t('apiMiddleware.conflictTitle', 'Conflito de dados');
      result.message = t('apiMiddleware.conflictMessage', 'Outro usuário pode ter modificado este registro.');
      result.suggestion = t('apiMiddleware.conflictSuggestion', 'Atualize a página e tente novamente.');
      result.technical_reference = 'HTTP 409';
      return result;
    }

    if (error.status === 429) {
      result.title = t('apiMiddleware.rateLimitTitle', 'Muitas tentativas');
      result.message = t('apiMiddleware.rateLimitMessage', 'Você fez muitas solicitações em um curto período.');
      result.suggestion = t('apiMiddleware.rateLimitSuggestion', 'Aguarde alguns minutos antes de tentar novamente.');
      result.technical_reference = 'HTTP 429';
      return result;
    }

    // Handle specific error codes from the API (SaaS Business Rules)
    if (error.code) {
      result.technical_reference = error.code;

      // SaaS Hierarchy & Permission Errors
      switch (error.code) {
        // Module & Feature Access
        case 'MODULE_NOT_AVAILABLE':
          result.title = t('saasErrors.moduleNotAvailable.title', 'Módulo indisponível');
          result.message = t('saasErrors.moduleNotAvailable.message', 'Este módulo não está habilitado para sua organização.');
          result.suggestion = t('saasErrors.moduleNotAvailable.suggestion', 'Entre em contato com seu distribuidor para habilitar este módulo.');
          return result;

        case 'MODULE_EXPIRED':
          result.title = t('saasErrors.moduleExpired.title', 'Módulo expirado');
          result.message = t('saasErrors.moduleExpired.message', 'O período de acesso a este módulo expirou.');
          result.suggestion = t('saasErrors.moduleExpired.suggestion', 'Solicite a renovação junto ao seu distribuidor.');
          return result;

        case 'FEATURE_NOT_AVAILABLE':
          result.title = t('saasErrors.featureNotAvailable.title', 'Funcionalidade indisponível');
          result.message = t('saasErrors.featureNotAvailable.message', 'Esta funcionalidade não está incluída no seu plano atual.');
          result.suggestion = t('saasErrors.featureNotAvailable.suggestion', 'Verifique os módulos contratados ou solicite upgrade.');
          return result;

        // Hierarchy Violations
        case 'HIERARCHY_VIOLATION':
          result.title = t('saasErrors.hierarchyViolation.title', 'Configuração não permitida');
          result.message = t('saasErrors.hierarchyViolation.message', 'Esta configuração excede os limites definidos pelo nível superior.');
          result.suggestion = t('saasErrors.hierarchyViolation.suggestion', 'Solicite permissão ao administrador do nível superior.');
          return result;

        case 'DEALER_LIMIT_EXCEEDED':
          result.title = t('saasErrors.dealerLimitExceeded.title', 'Limite do Dealer excedido');
          result.message = t('saasErrors.dealerLimitExceeded.message', 'O Dealer atingiu o limite máximo de clientes permitidos.');
          result.suggestion = t('saasErrors.dealerLimitExceeded.suggestion', 'Entre em contato com o Master para aumentar o limite.');
          return result;

        case 'TENANT_LIMIT_EXCEEDED':
          result.title = t('saasErrors.tenantLimitExceeded.title', 'Limite de recursos atingido');
          result.message = t('saasErrors.tenantLimitExceeded.message', 'Sua organização atingiu o limite de recursos contratados.');
          result.suggestion = t('saasErrors.tenantLimitExceeded.suggestion', 'Entre em contato com seu distribuidor para expandir seus limites.');
          return result;

        case 'APPLICATION_NOT_ASSIGNED':
          result.title = t('saasErrors.applicationNotAssigned.title', 'Aplicação não disponível');
          result.message = t('saasErrors.applicationNotAssigned.message', 'Esta aplicação não está liberada para o seu Dealer.');
          result.suggestion = t('saasErrors.applicationNotAssigned.suggestion', 'Solicite a liberação junto ao Master.');
          return result;

        case 'MODULE_NOT_GRANTED_TO_DEALER':
          result.title = t('saasErrors.moduleNotGrantedToDealer.title', 'Módulo não licenciado');
          result.message = t('saasErrors.moduleNotGrantedToDealer.message', 'Seu Dealer não possui licença para comercializar este módulo.');
          result.suggestion = t('saasErrors.moduleNotGrantedToDealer.suggestion', 'Verifique os módulos disponíveis ou solicite ao Master.');
          return result;

        case 'CONFIGURATION_EXCEEDS_PARENT':
          result.title = t('saasErrors.configurationExceedsParent.title', 'Configuração além do permitido');
          result.message = t('saasErrors.configurationExceedsParent.message', 'A configuração solicitada excede os limites definidos pelo nível superior na hierarquia.');
          result.suggestion = t('saasErrors.configurationExceedsParent.suggestion', 'Ajuste os valores ou solicite autorização ao administrador.');
          return result;

        case 'DURATION_EXCEEDS_ALLOWED':
          result.title = t('saasErrors.durationExceedsAllowed.title', 'Duração não permitida');
          result.message = t('saasErrors.durationExceedsAllowed.message', 'O período de validade informado excede o limite máximo configurado.');
          result.suggestion = t('saasErrors.durationExceedsAllowed.suggestion', 'Informe uma duração dentro do limite permitido.');
          return result;

        case 'LIFETIME_ACCESS_NOT_ALLOWED':
          result.title = t('saasErrors.lifetimeAccessNotAllowed.title', 'Acesso vitalício não permitido');
          result.message = t('saasErrors.lifetimeAccessNotAllowed.message', 'O sistema não permite configurar acesso sem data de expiração.');
          result.suggestion = t('saasErrors.lifetimeAccessNotAllowed.suggestion', 'Defina uma data de validade para o acesso.');
          return result;

        // Tenant & Dealer Specific
        case 'TENANT_NOT_ACTIVE':
          result.title = t('saasErrors.tenantNotActive.title', 'Cliente inativo');
          result.message = t('saasErrors.tenantNotActive.message', 'Esta operação não pode ser realizada pois o cliente está inativo.');
          result.suggestion = t('saasErrors.tenantNotActive.suggestion', 'Reative o cliente antes de continuar.');
          return result;

        case 'DEALER_NOT_ACTIVE':
          result.title = t('saasErrors.dealerNotActive.title', 'Distribuidor inativo');
          result.message = t('saasErrors.dealerNotActive.message', 'O Dealer vinculado está inativo no momento.');
          result.suggestion = t('saasErrors.dealerNotActive.suggestion', 'Entre em contato com o Master para resolver.');
          return result;

        case 'LICENSE_REVOKED':
          result.title = t('saasErrors.licenseRevoked.title', 'Licença cancelada');
          result.message = t('saasErrors.licenseRevoked.message', 'A licença desta organização foi cancelada.');
          result.suggestion = t('saasErrors.licenseRevoked.suggestion', 'Entre em contato com seu distribuidor.');
          return result;

        case 'MAX_USERS_REACHED':
          result.title = t('saasErrors.maxUsersReached.title', 'Limite de usuários atingido');
          result.message = t('saasErrors.maxUsersReached.message', 'Sua organização atingiu o número máximo de usuários permitidos.');
          result.suggestion = t('saasErrors.maxUsersReached.suggestion', 'Remova usuários inativos ou solicite aumento de limite.');
          return result;

        case 'MAX_DEVICES_REACHED':
          result.title = t('saasErrors.maxDevicesReached.title', 'Limite de dispositivos atingido');
          result.message = t('saasErrors.maxDevicesReached.message', 'O número máximo de dispositivos foi atingido.');
          result.suggestion = t('saasErrors.maxDevicesReached.suggestion', 'Remova dispositivos antigos ou solicite aumento de limite.');
          return result;

        default:
          // Try to translate using the generic apiErrors key
          const key = `apiErrors.${error.code}`;
          const translated = t(key);
          if (translated && translated !== key) {
            result.message = translated;
          }
      }
    } else if (error.message) {
      let rawMessage = error.message;
      try {
        if (rawMessage.startsWith('{')) {
          const parsed = JSON.parse(rawMessage);
          rawMessage = parsed.message || parsed.error || rawMessage;
        }
      } catch {}

      // Common English Backend Error Mapping
      const lowerRaw = rawMessage.toLowerCase();

      if (lowerRaw.includes('invalid username or password') || lowerRaw.includes('invalid credentials')) {
         result.title = t('login.errors.authFailed', 'Falha na autenticação');
         result.message = t('login.errors.failed', 'Usuário ou senha incorretos.');
         result.suggestion = t('apiMiddleware.authSuggestion', 'Faça login novamente.');
         result.technical_reference = rawMessage;
         return result;
      }

      if (lowerRaw.includes('provide the application name')) {
         result.title = t('applications.errors.missingName', 'Nome obrigatório');
         result.message = t('applications.errors.missingName', 'Informe o nome da aplicação.');
         result.technical_reference = rawMessage;
         return result;
      }

      // SaaS-specific string matching for backends that don't return error codes
      if (lowerRaw.includes('module not available') || lowerRaw.includes('module is not enabled')) {
         result.title = t('saasErrors.moduleNotAvailable.title', 'Módulo indisponível');
         result.message = t('saasErrors.moduleNotAvailable.message', 'Este módulo não está habilitado para sua organização.');
         result.suggestion = t('saasErrors.moduleNotAvailable.suggestion', 'Entre em contato com seu distribuidor.');
         result.technical_reference = rawMessage;
         return result;
      }

      if (lowerRaw.includes('exceeds the limit') || lowerRaw.includes('limit exceeded')) {
         result.title = t('saasErrors.tenantLimitExceeded.title', 'Limite de recursos atingido');
         result.message = t('saasErrors.tenantLimitExceeded.message', 'Sua organização atingiu o limite de recursos contratados.');
         result.suggestion = t('saasErrors.tenantLimitExceeded.suggestion', 'Entre em contato com seu distribuidor.');
         result.technical_reference = rawMessage;
         return result;
      }

      if (lowerRaw.includes('not allowed by hierarchy') || lowerRaw.includes('parent configuration')) {
         result.title = t('saasErrors.hierarchyViolation.title', 'Configuração não permitida');
         result.message = t('saasErrors.hierarchyViolation.message', 'Esta configuração excede os limites definidos pelo nível superior.');
         result.suggestion = t('saasErrors.hierarchyViolation.suggestion', 'Solicite permissão ao administrador do nível superior.');
         result.technical_reference = rawMessage;
         return result;
      }

      // If status is 400 (Validation), prefer a generic translated message
      if (error.status === 400) {
        result.title = t('apiMiddleware.validationTitle', 'Dados inválidos');
        result.message = t('apiMiddleware.validationMessage', 'Verifique os dados informados e tente novamente.');
        result.technical_reference = rawMessage;
      } else {
        // For other errors, use the raw message but try to be safe
        result.message = rawMessage;
      }
    }
  }

  // Handle standard Javascript Error
  if (error instanceof Error && !(error instanceof ApiError)) {
    result.technical_reference = error.name;
    // Don't expose stack traces or internal messages unless debugging
    // But since we want to be helpful, we might map common JS errors
    if (error.message.includes('Network Error') || error.message.includes('Failed to fetch')) {
        result.title = t('apiMiddleware.networkTitle', 'Erro de conexão');
        result.message = t('apiMiddleware.networkMessage', 'Não foi possível conectar ao servidor.');
        result.suggestion = t('apiMiddleware.networkSuggestion', 'Verifique sua conexão a internet.');
        result.technical_reference = 'NetworkError';
    }
  }

  return result;
}
